import React, { useState, useEffect, memo, useMemo } from 'react';
import { Modal, Upload, Button, Skeleton } from 'antd';
import { API } from '@service';
import {
  getHtmlUploadClientError,
  getHtmlUploadApiErrorMessage,
  isHtmlUploadSuccess,
  HTML_UPLOAD_ACCEPT,
  MAX_HTML_UPLOAD_MB,
  displayNameFromHtmlFileUrl,
  normalizeHtmlFileKey,
} from '@utils/htmlUploadValidation';

function UploadAndViewHtml(props) {
  const { changeHtmlLink, handleStatus, uploadAPI = API.uploadHtml } = props;
  const [hasUploaded, setHasUploaded] = useState(Boolean(props.value));
  const [showUpload, setShowUpload] = useState(false);
  const [extValid, setExtValid] = useState(true);
  const [clientHtmlError, setClientHtmlError] = useState(null);

  const [file, setFile] = useState(null);
  const [existingUploadFileName, setExistingUploadFileName] = useState(null);
  const [reupload, setReupload] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const storedDisplayName = useMemo(
    () => (props.value ? displayNameFromHtmlFileUrl(props.value) : ''),
    [props.value],
  );

  const isUploadMode =
    !hasUploaded || props.uploadOnlyNoEditAndDelete || reupload;
  const isEditMode =
    hasUploaded && !props.uploadOnlyNoEditAndDelete && !reupload;

  const modalTitle = isUploadMode
    ? reupload
      ? 'Replace HTML File'
      : 'Upload HTML'
    : 'Edit HTML';

  const openModal = () => {
    setClientHtmlError(null);
    setExtValid(true);
    setShowUpload(true);
  };

  const handleUploadFailure = (resOrError) => {
    const errMsg = getHtmlUploadApiErrorMessage(resOrError);
    setClientHtmlError(errMsg);
    setExtValid(false);
    if (handleStatus) {
      handleStatus(false);
    }
    setConfirmLoading(false);
  };

  const validateFile = (uploadFile) => {
    const clientErr = getHtmlUploadClientError(uploadFile);
    if (clientErr) {
      setClientHtmlError(clientErr);
      setExtValid(false);
      setFile(null);
      return false;
    }
    setClientHtmlError(null);
    setExtValid(true);
    const fileReader = new FileReader();
    fileReader.onload = () => {
      setFile(uploadFile);
    };
    fileReader.readAsBinaryString(uploadFile);
    return false;
  };

  const handleOk = async () => {
    setConfirmLoading(true);
    setClientHtmlError(null);
    if (!file) {
      setClientHtmlError('Please choose a file to upload.');
      setConfirmLoading(false);
      return;
    }
    const clientErr = getHtmlUploadClientError(file);
    if (clientErr) {
      setClientHtmlError(clientErr);
      setConfirmLoading(false);
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    const replaceFileName =
      existingUploadFileName ||
      (props.value ? normalizeHtmlFileKey(props.value) : null);
    if (replaceFileName) {
      formData.append('fileName', replaceFileName);
    }
    try {
      const res = await uploadAPI(formData);
      if (isHtmlUploadSuccess(res)) {
        setClientHtmlError(null);
        setExtValid(true);
        if (handleStatus) {
          handleStatus(true);
        }
        changeHtmlLink(res.fileUrl);
        setExistingUploadFileName(res.fileName);
        setFile(null);
        setReupload(false);
        setConfirmLoading(false);
        setShowUpload(false);
        setHasUploaded(true);
        return;
      }
      handleUploadFailure(res);
    } catch (e) {
      handleUploadFailure(e);
    }
  };

  const resetUploadState = () => {
    setFile(null);
    setClientHtmlError(null);
    setExtValid(true);
    setConfirmLoading(false);
  };

  const handleCancel = () => {
    resetUploadState();
    if (!hasUploaded && !reupload) {
      setShowUpload(false);
      setReupload(false);
      setHasUploaded(false);
      setExistingUploadFileName(null);
    } else if (reupload) {
      setReupload(false);
      setHasUploaded(true);
      setShowUpload(false);
    } else {
      setHasUploaded(true);
      setShowUpload(false);
    }
  };

  const handleReupload = () => {
    setReupload(true);
    setHasUploaded(false);
    resetUploadState();
  };

  const handleDelete = () => {
    setShowUpload(false);
    changeHtmlLink('');
    setFile(null);
    setExistingUploadFileName(null);
    setHasUploaded(false);
    setReupload(false);
    resetUploadState();
  };

  const renderFooter = () => {
    if (isUploadMode) {
      return (
        <div>
          <Button loading={confirmLoading} onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="primary"
            loading={confirmLoading}
            onClick={handleOk}
            disabled={!file || !extValid}
          >
            {reupload ? 'Replace' : 'Upload'}
          </Button>
        </div>
      );
    }
    return (
      <div style={{ width: '100%', textAlign: 'left' }}>
        <Button onClick={handleDelete}>Delete</Button>
        <Button type="primary" onClick={handleReupload}>
          Replace file
        </Button>
      </div>
    );
  };

  useEffect(() => {
    setHasUploaded(Boolean(props.value));
    if (props.value) {
      setExistingUploadFileName(normalizeHtmlFileKey(props.value));
    } else {
      setExistingUploadFileName(null);
    }
  }, [props.value]);

  return (
    <>
      <div className="upload-pdf-text" onClick={openModal}>
        {!hasUploaded || props.uploadOnlyNoEditAndDelete
          ? 'Upload HTML'
          : 'Edit HTML'}
      </div>

      <Modal
        className={`upload-modal upload-html${
          isEditMode ? ' edit-html-modal' : ''
        }`}
        title={modalTitle}
        visible={showUpload}
        onCancel={handleCancel}
        footer={renderFooter()}
        destroyOnClose
      >
        {isEditMode ? (
          storedDisplayName ? (
            <div className="upload-html-file-card">
              <span className="appkiticon icon-document-fill" aria-hidden />
              <span className="upload-html-file-card-name">
                {storedDisplayName}
              </span>
            </div>
          ) : (
            <div className="file-list wrap-anywhere">
              <Skeleton paragraph={false} active />
            </div>
          )
        ) : (
          <>
            {reupload && storedDisplayName ? (
              <div className="upload-html-current-file">
                <span className="upload-html-current-file-label">
                  Current file
                </span>
                <span className="upload-html-current-file-name">
                  {storedDisplayName}
                </span>
              </div>
            ) : null}
            <Upload.Dragger
              beforeUpload={(f) => validateFile(f)}
              maxCount={1}
              showUploadList={false}
              accept={HTML_UPLOAD_ACCEPT}
            >
              <span className="upload-html-dragger-title">
                Drag and drop or <span>choose your HTML file</span>
              </span>
              <div className="upload-tips">
                HTML (.html, .htm) only · Maximum file size {MAX_HTML_UPLOAD_MB}{' '}
                MB
              </div>
            </Upload.Dragger>
            {file ? (
              <div className="upload-html-selected-file">
                <span className="upload-html-selected-file-label">
                  Selected file
                </span>
                <span className="upload-html-selected-file-name">
                  {file.name}
                </span>
              </div>
            ) : null}
            {clientHtmlError ? (
              <div className="upload-html-error" role="alert">
                {clientHtmlError}
              </div>
            ) : null}
          </>
        )}
      </Modal>
    </>
  );
}

export default memo(UploadAndViewHtml);
